/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import * as _ from 'lodash';
import aesjs from 'aes-js';
import Fingerprint2 from 'fingerprintjs2';
import UAParser from 'ua-parser-js';
import md5 from 'js-md5';

const parser = new UAParser();
const userAgent = navigator.userAgent.toLowerCase();
const BASE_URL =
  /safari/.test(userAgent) && !/chrome/.test(userAgent)
    ? process.env.REACT_APP_BASE_URL
    : process.env.REACT_APP_BASE_URL_API;

const APPID = process.env.REACT_APP_APP_ID;

const VER_CODE = [
  52, 100, 48, 52, 98, 50, 57, 98, 48, 49, 55, 100, 52, 56, 100, 101, 56, 48, 50, 51, 100, 102, 51, 99, 55, 57, 50, 49,
  98, 48, 98, 100,
];

type ResType<T> = {
  _sys_status?: string | number;
  errorCode?: string | number;
  sys_status?: string | number;
} & T;

class BaseServiceApi {
  lock: any[] = [];

  constructor() {
    this.lock = [];
  }

  constructRequestURL(url: string) {
    return this.getBaseApiUrl() + url;
  }

  getBaseApiUrl() {
    return BASE_URL || '';
  }

  urlEncode(data: any) {
    if (!data) {
      return '';
    }

    const params = new URLSearchParams();
    // eslint-disable-next-line guard-for-in, no-restricted-syntax
    for (const key in data) {
      params.append(key, data[key]);
    }

    return params.toString();
  }

  get<T>(url: string, data: any) {
    return this.authFetch<T>('GET', url, data);
  }

  post<T>(url: string, data: any) {
    return this.authFetch<T>('POST', url, data);
  }

  put<T>(url: string, data: any) {
    return this.authFetch<T>('PUT', url, data);
  }

  delete<T>(url: string, data: any) {
    return this.authFetch<T>('DELETE', url, data);
  }

  async authFetch<T>(method: string, url: string, data: any) {
    let response = await this.fetch(method, url, data);
    // console.log('authFetch', response);
    // eslint-disable-next-line no-underscore-dangle
    let status = response._sys_status || response.errorCode || response.sys_status || '';
    if (typeof status !== 'string') {
      status = status.toString();
    }
    if (status === '403') {
      if (this.lock.length) {
        response = await this.waitLoad(response, method, url, data);
      } else {
        await this.register();
        response = await this.fetch(method, url, data);
      }
    }
    const result = _.cloneDeep(response); // fix body recursion
    response.body = result; // backward compatibility
    return Promise.resolve(response as ResType<T>);
  }

  async register() {
    const url = '/api/ch/r';

    this.lock.push(null);
    let params = await this.authParams();
    let auth = await this.fetch('POST', url, params as any, 'formdata');
    while (auth._sys_status !== '0') {
      await this.sleep(1000);
      params = await this.authParams();
      auth = await this.fetch('POST', url, params as any);
    }
    this.lock.pop();
  }

  // eslint-disable-next-line max-params
  async waitLoad(response: any, method: any, url: any, data: any) {
    let elapse = 0;
    while (this.lock.length) {
      await this.sleep(100);
      elapse += 100;

      if (elapse > 1000 * 10) {
        throw new Error("can't get authentication from server");
      }
    }

    return this.fetch(method, url, data);
  }

  sleep(ms: number) {
    // eslint-disable-next-line no-promise-executor-return
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  padding(val: string) {
    if (val.length % 16 === 0) return val;

    const p = 16 - (val.length % 16);
    const pv = [];
    for (let i = 0; i < p; i += 1) {
      pv.push('\n');
    }

    return val + pv.join('');
  }

  aesCipher(data: any) {
    const iv = [];
    let now: any = new Date();
    now = now.valueOf() * 1000 + parseInt((Math.random() * 1000) as any, 10);
    now = now.toString();
    for (let i = 0; i < 16; i += 1) {
      iv.push(parseInt(now[i] + now[i], 10));
    }
    iv.reverse();

    const key = VER_CODE;

    // eslint-disable-next-line new-cap
    const aesCBC = new aesjs.ModeOfOperation.cbc(key, iv);

    let val: any = this.padding(data);
    val = aesjs.utils.utf8.toBytes(val);
    let result: any = aesCBC.encrypt(val);
    result = aesjs.utils.hex.fromBytes(result);

    const ivHex = aesjs.utils.hex.fromBytes(iv);

    result = ivHex + result;

    return result;
  }

  async genDeviceUUID() {
    await this.sleep(1000);

    return new Promise((resolve, reject) => {
      Fingerprint2.get((components: any) => {
        const values = components.map((component: any) => component.value);
        const result = Fingerprint2.x64hash128(values.join(''), 31);
        resolve(result);
      });
    });
  }

  async getDeviceUUID() {
    const key = localStorage.getItem('k');
    let deviceUUID = null;
    if (!key) {
      deviceUUID = (await this.genDeviceUUID()) as string;
      localStorage.setItem('k', btoa(deviceUUID));
    } else {
      deviceUUID = atob(key);
    }

    return Promise.resolve(deviceUUID);
  }

  hexEncode(str: string) {
    let result = '';
    for (let i = 0; i < str.length; i += 1) {
      result += str.charCodeAt(i).toString(16);
    }
    return result;
  }

  getLang() {
    const lang = navigator.language;
    if (!lang) return 'en';

    switch (lang) {
      case 'zh-CN':
      case 'zh-SG':
        return 'zh_CN';
      case 'zh-TW':
      case 'zh-HK':
        return 'zh_HK';
      default:
        return 'en';
    }
  }

  async authParams() {
    const e = this.aesCipher.bind(this);

    const du = await this.getDeviceUUID();
    const unix_time = Math.round(new Date().getTime() / 1000);

    const ad = md5(APPID + String(unix_time));

    const result = {
      dt: '5',
      du,
      dn: e(
        `${window.navigator.platform}:${parser.getBrowser().name}@${parser.getBrowser().version} ${navigator.language}`,
      ),
      do: e(`${parser.getOS().name}`),
      dv: e(`${parser.getOS().version}`),
      dw: e(window.outerWidth.toString()),
      dh: e(window.outerHeight.toString()),
      ad,
      av: this.hexEncode('0.0.1'),
      an: '31',
      ln: e(this.getLang()),
      t: unix_time,
    };

    return result;
  }

  // eslint-disable-next-line max-params
  async fetch(method: string, url: string, data: string, type?: string) {
    const params: any = {
      method,
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      redirect: 'follow',
      referrer: 'no-referrer',
    };

    if (method === 'GET') {
      data = this.urlEncode(data);
      url = `${url}?${data}`;
    }else if (type === 'formdata') {
      data = this.urlEncode(data);
      params.body = data;
    } else {
      data = JSON.stringify(data);
      params.body = data;
    }

    return fetch(this.constructRequestURL(url), params)
      .then((response) => this.resolveData(response.json()))
      .catch((error) => {
        throw error;
      });
  }

  resolveData(data: any) {
    return data;
  }
}

// let BaseServiceApi = new BaseServiceApi();
export default new BaseServiceApi();
